import { useMemo, useState } from "react";

import SpacePath from "components/SpacePath";
import Box from "ds/components/Box";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import { SpacesContext } from "views/Account/SpacesProvider";
import useTypedContext from "hooks/useTypedContext";

import styles from "./styles.module.css";
import AssignRoleFormFormEdit from "./Edit";
import AssignRoleFormDeleteAction from "./DeleteAction";
import { AssignRoleFormEditFields, CustomRoleAction } from "./types";

type AssignRoleFormListItemProps = {
  roleId: string;
  roleName: string;
  spaceId: string;
  spaceName: string;
  hasAccessToSpace: boolean;
  roleAssignmentId: string;
  availableRoles: Array<{ id: string; name: string }>;
  selectableRoleIds?: Array<string>;
  selectableSpaceIds?: Array<string>;
  setIsDirty: (id: string, isDirty: boolean) => void;
  onEdit?: (formData: AssignRoleFormEditFields, roleAssignmentId: string) => void;
  onDelete: (roleAssignmentId: string, roleName: string) => void;
  customActions?: CustomRoleAction[];
};

const AssignRoleFormListItem = ({
  roleId,
  spaceName,
  spaceId,
  hasAccessToSpace,
  roleName,
  roleAssignmentId,
  availableRoles,
  selectableRoleIds,
  selectableSpaceIds,
  setIsDirty,
  onEdit,
  onDelete,
  customActions,
}: AssignRoleFormListItemProps) => {
  const { manageableSpaces } = useTypedContext(SpacesContext);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleSetEditMode = () => setIsEditMode(true);
  const handleUnsetEditMode = () => setIsEditMode(false);

  const selectableSpacesForEdit = useMemo(() => {
    if (selectableSpaceIds) {
      return manageableSpaces.filter(
        (space) => selectableSpaceIds.includes(space.id) || space.id === spaceId
      );
    }

    return manageableSpaces;
  }, [manageableSpaces, selectableSpaceIds, spaceId]);

  const selectableRolesForEdit = useMemo(() => {
    if (selectableRoleIds) {
      return availableRoles.filter(
        (role) => selectableRoleIds.includes(role.id) || role.id === roleId
      );
    }

    return availableRoles;
  }, [availableRoles, roleId, selectableRoleIds]);

  if (isEditMode && onEdit) {
    return (
      <AssignRoleFormFormEdit
        roleAssignmentId={roleAssignmentId}
        setIsDirty={setIsDirty}
        selectableRoles={selectableRolesForEdit}
        selectableSpaces={selectableSpacesForEdit}
        roleId={roleId}
        spaceId={spaceId}
        onEditCancel={handleUnsetEditMode}
        onEditSuccess={handleUnsetEditMode}
        onEdit={onEdit}
      />
    );
  }

  return (
    <Box
      direction="row"
      padding="large"
      gap="x-large"
      align="center"
      justify="between"
      className={styles.listItem}
      fullWidth
    >
      <Box direction="column" gap="small" className={styles.roleName} zeroMinWidth>
        <TextEllipsis tooltipWidthMode="maxWidthSm" tooltip={roleName}>
          {(props) => (
            <Typography {...props} variant="p-t6" tag="span">
              {roleName}
            </Typography>
          )}
        </TextEllipsis>
      </Box>
      <Box
        direction="row"
        gap="x-large"
        align="center"
        justify="end"
        className={styles.spaceAndActions}
        zeroMinWidth
      >
        <Box zeroMinWidth overflowHidden>
          <SpacePath
            spaceId={spaceId}
            fallbackName={spaceName}
            target="_blank"
            disabled={!hasAccessToSpace}
            tooltip={!hasAccessToSpace ? "You don't have access to this space" : undefined}
          />
        </Box>
        <DropdownMenuEllipsis tooltip="Role assignment actions" buttonVariant="ghost">
          {customActions?.map((action) => (
            <DropdownMenuItem
              key={action.label}
              onAction={() => action.onAction(roleAssignmentId)}
              danger={action.isDanger}
            >
              {action.label}
            </DropdownMenuItem>
          ))}
          {!!onEdit && <DropdownMenuItem onAction={handleSetEditMode}>Edit role</DropdownMenuItem>}
          <AssignRoleFormDeleteAction onDelete={() => onDelete(roleAssignmentId, roleName)} />
        </DropdownMenuEllipsis>
      </Box>
    </Box>
  );
};

export default AssignRoleFormListItem;
